'use client';

import { type ReactNode, useEffect, useRef, useState } from 'react';

import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * QA #346 — Overlays left / right nav arrows on top of a horizontally
 * scrolling rail (Collection Rack tiles) so viewers can page through
 * additional products without a scroll wheel. Arrows appear whenever:
 *   - the rail actually overflows (contentWidth > containerWidth)
 *   - and the current scroll direction has more content to reveal
 *     (the "at start" hides ←, "at end" hides →)
 *
 * No pointer-type gate — QA: "arrows should be available whenever
 * additional products exist beyond the visible area." (The previous
 * `(pointer: fine)` gate also hid them under DevTools device
 * emulation, reading as 'arrows missing'.)
 *
 * The arrows scroll the inner rail (`.no-scrollbar` — the direct scroll
 * container the parent renders) by ~one visible width using the
 * browser's smooth-scroll behavior.
 */
export function RackScrollArrows({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    // The scroll container is the first direct child of the wrapper.
    const rail = wrap.firstElementChild as HTMLElement | null;
    if (!rail) return;

    const compute = () => {
      const overflow = rail.scrollWidth - rail.clientWidth;
      setCanScrollLeft(rail.scrollLeft > 4);
      setCanScrollRight(overflow > 4 && rail.scrollLeft < overflow - 4);
    };
    compute();

    rail.addEventListener('scroll', compute, { passive: true });
    const resizeObserver = new ResizeObserver(compute);
    resizeObserver.observe(rail);
    // The rail's BOX doesn't change when tiles append (async product
    // loads, infinite-scroll pages) — only its scrollWidth does, which
    // ResizeObserver can't see. Watch the child list so appended tiles
    // re-measure and the → arrow appears the moment content overflows.
    const mutationObserver = new MutationObserver(compute);
    mutationObserver.observe(rail, { childList: true, subtree: true });
    return () => {
      rail.removeEventListener('scroll', compute);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [children]);

  const scrollBy = (direction: 1 | -1) => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const rail = wrap.firstElementChild as HTMLElement | null;
    if (!rail) return;
    // Page by ~90% of the visible width so the next set of tiles is
    // fully in view without leaving a partial tile at the edge.
    const step = rail.clientWidth * 0.9;
    rail.scrollBy({ left: direction * step, behavior: 'smooth' });
  };

  const showArrows = canScrollLeft || canScrollRight;

  return (
    <div ref={wrapRef} className={`relative ${className ?? ''}`}>
      {children}
      {/* Chevrons stay WHITE regardless of the creator's theme — they
          sit inside an always-black/55 disc, and painting them with the
          card's text color (previous `arrowColor` prop) made them
          invisible on dark-text themes (QA: "no navigation arrows"). */}
      {showArrows && canScrollLeft ? (
        <button
          type="button"
          aria-label="Scroll left"
          onClick={() => scrollBy(-1)}
          className="absolute left-1 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-black/55 text-white shadow-md backdrop-blur-sm transition hover:bg-black/75"
        >
          <ChevronLeft className="h-5 w-5" aria-hidden />
        </button>
      ) : null}
      {showArrows && canScrollRight ? (
        <button
          type="button"
          aria-label="Scroll right"
          onClick={() => scrollBy(1)}
          className="absolute right-1 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-black/55 text-white shadow-md backdrop-blur-sm transition hover:bg-black/75"
        >
          <ChevronRight className="h-5 w-5" aria-hidden />
        </button>
      ) : null}
    </div>
  );
}
